import { Controller } from "@hotwired/stimulus"
import { AreaSeries, LineSeries, createChart } from "lightweight-charts"

// Centralized palette so marketing tweaks are one-stop.
const BRAND = {
  green: "#556B2F",
  yellow: "#EAB308",
  dark: "#171717",
  darkCard: "#1F1F1F",
  white: "#FAFAFA",
}

const COLORS = {
  chartBackground: BRAND.white,
  text: BRAND.dark,
  grid: "rgba(23, 23, 23, 0.08)",
  border: "rgba(23, 23, 23, 0.18)",
  crosshairLine: "rgba(234, 179, 8, 0.55)",

  bandFill: "rgba(203, 222, 255, 0.6)",
  bandMarkerFill: "rgb(255, 255, 255)",

  medianLine: "rgba(48,104,235)",

  ammoLine: "rgb(248, 104, 11)",
}

export default class extends Controller {
  static targets = ["container"]
  static values = {
    band: Array,
    median: Array,
    ammo: Array,
    logoUrl: String,
  }

  connect() {
    if (!document.getElementById("hide-tv-attr-logo")) {
      const style = document.createElement("style")
      style.id = "hide-tv-attr-logo"
      style.textContent = "#tv-attr-logo { display: none !important; }"
      document.head.appendChild(style)
    }

    this._ensureWatermark()
    this._ensureLogo()

    this.chart = createChart(this.containerTarget, {
      autoSize: true,
      layout: {
        background: { type: "solid", color: COLORS.chartBackground },
        textColor: COLORS.text,
      },
      grid: {
        vertLines: { color: COLORS.grid },
        horzLines: { color: COLORS.grid },
      },
      rightPriceScale: {
        borderColor: COLORS.border,
        textColor: COLORS.text,
        scaleMargins: { top: 0.12, bottom: 0.18 },
      },
      timeScale: { borderColor: COLORS.border },
      crosshair: {
        vertLine: { color: COLORS.crosshairLine },
        horzLine: { color: COLORS.crosshairLine },
      },
    })

    const band = Array.isArray(this.bandValue) ? this.bandValue : []
    const highSeriesData = band.map((p) => ({ time: p.time, value: p.topValue }))
    const lowSeriesData = band.map((p) => ({ time: p.time, value: p.bottomValue }))

    this.highBandSeries = this.chart.addSeries(AreaSeries, {
      lineColor: COLORS.bandFill,
      topColor: COLORS.bandFill,
      bottomColor: COLORS.bandFill,
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      crosshairMarkerBackgroundColor: COLORS.bandMarkerFill,
      crosshairMarkerBorderColor: COLORS.bandFill,
    })

    this.lowBandMaskSeries = this.chart.addSeries(AreaSeries, {
      lineColor: "rgba(0, 0, 0, 0)",
      topColor: "rgba(250, 250, 250, 0.92)",
      bottomColor: "rgba(250, 250, 250, 0.92)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      crosshairMarkerBackgroundColor: COLORS.bandMarkerFill,
      crosshairMarkerBorderColor: COLORS.bandFill,
    })

    this.medianSeries = this.chart.addSeries(LineSeries, {
      color: COLORS.medianLine,
      lineWidth: 2,
      priceLineVisible: false,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      crosshairMarkerBackgroundColor: COLORS.medianLine,
      crosshairMarkerBorderColor: COLORS.medianLine,
    })

    this.ammoSeries = this.chart.addSeries(LineSeries, {
      color: COLORS.ammoLine,
      lineWidth: 2,
      priceLineVisible: false,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      crosshairMarkerBackgroundColor: BRAND.yellow,
      crosshairMarkerBorderColor: BRAND.yellow,
    })

    this.highBandSeries.setData(highSeriesData)
    this.lowBandMaskSeries.setData(lowSeriesData)
    this.medianSeries.setData(Array.isArray(this.medianValue) ? this.medianValue : [])
    this.ammoSeries.setData(Array.isArray(this.ammoValue) ? this.ammoValue : [])

    requestAnimationFrame(() => {
      if (this.chart) this.chart.timeScale().fitContent()
    })
  }

  _ensureLogo() {
    if (!this.hasLogoUrlValue || !this.logoUrlValue) return
    if (this.logoEl) return

    const img = document.createElement("img")
    img.src = this.logoUrlValue
    img.alt = "AmmoSquared"
    img.decoding = "async"
    img.loading = "lazy"
    img.style.position = "absolute"
    img.style.left = "10px"
    img.style.bottom = "39px"
    img.style.height = "19px"
    img.style.width = "auto"
    img.style.margin = "0"
    img.style.padding = "0"
    img.style.border = "0"
    img.style.opacity = "0.95"
    img.style.pointerEvents = "none"
    img.style.zIndex = "3"

    this.element.appendChild(img)
    this.logoEl = img
  }

  _ensureWatermark() {
    if (!this.hasLogoUrlValue || !this.logoUrlValue) return
    if (this.watermarkEl) return

    const img = document.createElement("img")
    img.src = this.logoUrlValue
    img.alt = "AmmoSquared"
    img.decoding = "async"
    img.loading = "lazy"
    img.style.position = "absolute"
    img.style.left = "50%"
    img.style.top = "50%"
    img.style.transform = "translate(-50%, -50%)"
    img.style.width = "220px"
    img.style.maxWidth = "55%"
    img.style.height = "auto"
    img.style.opacity = "0.05"
    img.style.pointerEvents = "none"
    img.style.zIndex = "2"
    img.style.filter = "grayscale(100%)"

    this.element.appendChild(img)
    this.watermarkEl = img
  }

  disconnect() {
    if (this.chart) {
      this.chart.remove()
      this.chart = null
    }

    if (this.logoEl) {
      this.logoEl.remove()
      this.logoEl = null
    }

    if (this.watermarkEl) {
      this.watermarkEl.remove()
      this.watermarkEl = null
    }
  }
}


